import React, {type Dispatch, type SetStateAction, useEffect, useMemo} from 'react';
import {useDispatch} from 'react-redux';

import clsx from 'clsx';
import {SelectionRow, clearSelection} from 'src/components/data-table-selection';
import {ToolsRibbonComponent} from 'src/components/tools-ribbon';
import {
    AssignAccountsEntitlementsTool,
    AssignUsersEntitlementsTool,
    DischargeAccountsEntitlementsTool,
    DischargeUsersEntitlementsTool,
} from 'src/modules/bulk-update';
import {useOpenEntitlementDrawer} from 'src/modules/entitlements';
import {closeDrawer} from 'src/modules/entitlements/components/drawer/entitlement-drawer.slice';
import {
    HighlightedText,
    MainColumnAssignedResourceCell,
    MainColumnFreeResourceCell,
    MainColumnGroupCell,
    MainColumnHeader,
} from 'src/modules/entitlements/components/main-column';
import {mainColumnCellGroupRow} from 'src/modules/entitlements/components/main-column/main-column-cell.module.styl';

import {FlatButton} from '@pb/kit-component-button';
import {
    Cell,
    type CellOptions,
    type Column,
    DataTable,
    type HeaderCellOptions,
    type RowsParams,
    type StopResizeParams,
    TableHeader,
} from '@pb/kit-component-data-table-v2';
import {DropdownMenu, DropdownMenuItem} from '@pb/kit-component-dropdown-menu';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import {StickyElement} from '@pb/kit-component-sticky-element';
import {useShown} from '@pb/kit-hooks-use-shown';


import {manageEntitlementsToolRibbonSticky} from '../../manage-entitlements.module.styl';

import {RolesGrantsResourcesPagination} from './components/roles-grants-resources.pagination';
import {COLUMNS, COLUMNS_DATA, HEADERS, SELECTION_KEY} from './constants';
import {useEntitlementsTransfer} from './hooks/useEntitlementsTransfer';

import type {ResourceView} from 'src/modules/entitlements/typedef';

type Props = {
    dataTableId: string,

    expandedRoleIds: string[],
    setExpandedRoleIds: Dispatch<SetStateAction<string[]>>,

    rows: ResourceView[],
    totalCount: number,
    shortHeader: React.ReactElement,
    resourceFilter: string,

    getColumnWidthByColumnId: (columnId: string) => number,
    onStopResize: (params: StopResizeParams) => void,
};

export const RolesGrantsResourcesView = (props: Props) => {
    const dispatch = useDispatch();
    const {
        rows,
        expandedRoleIds,
        setExpandedRoleIds,
        totalCount,
        shortHeader,
        resourceFilter,
        getColumnWidthByColumnId,
        onStopResize,
        dataTableId,
    } = props;

    const {
        exportErrorMessage,
        importButton,
        renderExportButton,
        transferModals,
    } = useEntitlementsTransfer({rows, selectionKey: SELECTION_KEY});

    const {
        shown: assignToAccountsShown,
        onClick: openAssignToAccounts,
        onClose: closeAssignToAccounts,
    } = useShown();
    const {
        shown: dischargeFromAccountsShown,
        onClick: openDischargeFromAccounts,
        onClose: closeDischargeFromAccounts,
    } = useShown();

    const {
        shown: assignToUsersShown,
        onClick: openAssignToUsers,
        onClose: closeAssignToUsers,
    } = useShown();
    const {
        shown: dischargeFromUsersShown,
        onClick: openDischargeFromUsers,
        onClose: closeDischargeFromUsers,
    } = useShown();

    const {openResourceDrawer, openGrantDrawer, openRoleDrawer} = useOpenEntitlementDrawer();

    const renderedRows = useMemo(() => (rows
        .filter(entitlement => {
            const record = JSON.parse(entitlement.recordId);
            return (record.roleId && record.resourceId
                ? expandedRoleIds.includes(record.roleId)
                : true
            );
        })
        .map(entitlement => ({
            id: entitlement.recordId,
            columnValues: {
                title: entitlement.title,
                description: entitlement.description,
                grant: entitlement.grantTitle,
                status: entitlement.statusTitle,
                restriction: entitlement.restrictionTitle,
            },
        }))
    ), [rows, expandedRoleIds]);

    const selectableRows = useMemo(() => (renderedRows
        .filter(row => {
            const record = JSON.parse(row.id);
            return record.roleId ? !record.resourceId : true;
        })
        .map(({id}) => id) || []
    ), [renderedRows]);

    useEffect(() => {
        return () => {
            dispatch(closeDrawer());
            dispatch(clearSelection(SELECTION_KEY));
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            {exportErrorMessage}
            <StickyElement className={`sticky-tool-ribbon ${manageEntitlementsToolRibbonSticky}`}>
                <ToolsRibbonComponent {...{
                    rows: selectableRows,
                    selectionKey: SELECTION_KEY,
                    showSelectAll: false,
                    totalCount: selectableRows.length,
                    filtersTools: importButton,
                }}>
                    {ids => (
                        <Group>
                            <DropdownMenu
                                anchor={({positionRef, getAnchorProps, anchorRef}) => (
                                    <FlatButton
                                        endIcon={<Icon type={Icons.ANGLE_DOWN} ref={positionRef}/>}
                                        {...getAnchorProps()}
                                        ref={anchorRef}>
                                        <b>Assign Entitlements</b>
                                    </FlatButton>
                                )}>
                                <DropdownMenuItem
                                    onClick={openAssignToAccounts}
                                    renderCaption="To Accounts"
                                />
                                <DropdownMenuItem
                                    onClick={openAssignToUsers}
                                    renderCaption="To Users"
                                />
                            </DropdownMenu>
                            <DropdownMenu anchor={({positionRef, getAnchorProps, anchorRef}) => (
                                <FlatButton
                                    endIcon={<Icon type={Icons.ANGLE_DOWN} ref={positionRef}/>}
                                    {...getAnchorProps()}
                                    ref={anchorRef}>
                                    <b>Unassign Entitlements</b>
                                </FlatButton>
                            )}>
                                <DropdownMenuItem
                                    onClick={openDischargeFromAccounts}
                                    renderCaption="From Accounts"
                                />
                                <DropdownMenuItem
                                    onClick={openDischargeFromUsers}
                                    renderCaption="From Users"
                                />
                            </DropdownMenu>
                            <AssignAccountsEntitlementsTool
                                ids={ids as string[]}
                                shown={assignToAccountsShown}
                                onClose={closeAssignToAccounts}
                            />
                            <DischargeAccountsEntitlementsTool
                                ids={ids as string[]}
                                shown={dischargeFromAccountsShown}
                                onClose={closeDischargeFromAccounts}
                            />
                            <AssignUsersEntitlementsTool
                                ids={ids as string[]}
                                shown={assignToUsersShown}
                                onClose={closeAssignToUsers}
                            />
                            <DischargeUsersEntitlementsTool
                                ids={ids as string[]}
                                shown={dischargeFromUsersShown}
                                onClose={closeDischargeFromUsers}
                            />
                            {renderExportButton(ids)}
                        </Group>
                    )}
                </ToolsRibbonComponent>
            </StickyElement>
            <DataTable
                id={dataTableId}
                data={{
                    rows: renderedRows, columns: COLUMNS_DATA.map((column: Column) => ({
                        ...column,
                        width: getColumnWidthByColumnId(column.columnId),
                    })),
                }}
                getHeaderTopOffset={() => 193}
                onStopResize={onStopResize}
                headers={({column}: {column: Column}, cellOptions: HeaderCellOptions) => {
                    switch (column.columnId) {
                        case COLUMNS.TITLE:
                            return (
                                <Cell
                                    {...cellOptions}
                                    key={`${column.columnId}`}
                                    column={column}
                                    resizable={true}>
                                    <MainColumnHeader selection={<div/>}>
                                        {shortHeader}
                                    </MainColumnHeader>
                                </Cell>
                            );
                        default:
                            return (
                                <Cell
                                    {...cellOptions}
                                    key={`${column.columnId}`}
                                    column={column}
                                    resizable={true}>
                                    <TableHeader indent="none" className="search-header">
                                        <Group alignItems="center">
                                            <strong>{HEADERS[column.columnId]}</strong>
                                        </Group>
                                    </TableHeader>
                                </Cell>
                            );
                    }
                }}
                renderRows={(rowsParams: RowsParams, cellOptions: CellOptions) => {
                    const {column: {columnId}, rowIndex} = rowsParams;
                    const value = rowsParams.row.columnValues[columnId] as string;
                    const column = rowsParams.column;
                    const rowId = rowsParams.row.id;
                    const cellKey = `${rowId}-${columnId}`;

                    const record = JSON.parse(rowId as string);

                    const isGroupRow = record.roleId && !record.resourceId;
                    const roleId = isGroupRow ? record.roleId : undefined;
                    const isAssignedResource = !!(record.roleId && record.resourceId);

                    const cellClasses = clsx('search-result-cell', [
                        isGroupRow && mainColumnCellGroupRow,
                    ]);

                    switch (columnId) {
                        case COLUMNS.TITLE:
                            if (isGroupRow) {
                                return (
                                    <MainColumnGroupCell
                                        key={cellKey}
                                        {...{
                                            selection: (
                                                <SelectionRow
                                                    rows={selectableRows}
                                                    index={rowIndex}
                                                    entityId={rowId}
                                                    selectionKey={SELECTION_KEY}
                                                    totalCount={selectableRows.length}
                                                />
                                            ),
                                            isExpanded: expandedRoleIds.includes(roleId as string),
                                            onExpand: () => setExpandedRoleIds(
                                                prev => [...prev, roleId as string],
                                            ),
                                            onCollapse: () => setExpandedRoleIds(
                                                prev => prev.filter(id => id !== roleId),
                                            ),
                                            cellOptions: {...cellOptions, column},
                                        }}>
                                        <EllipsisTextHint title={value}>
                                            <a onClick={() => openRoleDrawer(record.roleId)}>
                                                <strong>{value}</strong>
                                            </a>
                                        </EllipsisTextHint>
                                    </MainColumnGroupCell>
                                );
                            }

                            if (isAssignedResource) {
                                return (
                                    <MainColumnAssignedResourceCell
                                        key={cellKey}
                                        {...{cellOptions: {...cellOptions, column}}}>
                                        <EllipsisTextHint title={value}>
                                            <a onClick={() => openResourceDrawer(record.resourceId)}>
                                                <HighlightedText text={value} highlightedText={resourceFilter}/>
                                            </a>
                                        </EllipsisTextHint>
                                    </MainColumnAssignedResourceCell>
                                );
                            }

                            return (
                                <MainColumnFreeResourceCell
                                    key={cellKey}
                                    {...{
                                        selection: (
                                            <SelectionRow
                                                rows={selectableRows}
                                                index={rowIndex}
                                                entityId={rowId}
                                                selectionKey={SELECTION_KEY}
                                                totalCount={selectableRows.length}
                                            />
                                        ),
                                        cellOptions: {...cellOptions, column},
                                        groupRow: isGroupRow,
                                    }}>
                                    <div className={isAssignedResource ? 'pdx-l' : undefined}>
                                        <EllipsisTextHint title={value}>
                                            <a onClick={() => openResourceDrawer(record.resourceId)}>
                                                <HighlightedText text={value} highlightedText={resourceFilter}/>
                                            </a>
                                        </EllipsisTextHint>
                                    </div>
                                </MainColumnFreeResourceCell>
                            );
                        case COLUMNS.GRANT:
                            return (
                                <Cell
                                    {...cellOptions}
                                    key={cellKey}
                                    column={column}
                                    className={cellClasses}>
                                    <EllipsisTextHint title={value}>
                                        <a onClick={() => openGrantDrawer(record.grantId)}>
                                            {value}
                                        </a>
                                    </EllipsisTextHint>
                                </Cell>
                            );
                        default:
                            return (
                                <Cell
                                    {...cellOptions}
                                    key={cellKey}
                                    column={column}
                                    className={cellClasses}>
                                    <EllipsisTextHint title={value}>
                                        {value}
                                    </EllipsisTextHint>
                                </Cell>
                            );
                    }
                }}
                paginationStripe={<RolesGrantsResourcesPagination totalCount={totalCount}/>}
            />
            {transferModals}
        </>
    );
};
